'use client';

import { useEffect, useState, useRef, MouseEvent, RefObject } from 'react';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';
import { FaChevronDown, FaChevronUp } from '@/components/icons';

interface TocEntry {
  id: string;
  text: string;
}

interface TableOfContentsProps {
  /** Ref to the article container whose H2 headings should be listed. */
  containerRef: RefObject<HTMLElement | null>;
  title?: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/**
 * Auto-generated Table of Contents for blog articles.
 * Scans the referenced container for H2 headings on mount, assigns anchor
 * ids where missing, and highlights the section currently in view.
 * Renders nothing when the article has fewer than 2 H2 sections.
 */
export default function TableOfContents({ containerRef, title = 'Table of Contents' }: TableOfContentsProps) {
  const [entries, setEntries] = useState<TocEntry[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [isOpen, setIsOpen] = useState(true);
  const visibleIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const headings = Array.from(container.querySelectorAll('h2'));
    const seen = new Map<string, number>();

    const built: TocEntry[] = headings.map((heading) => {
      const text = heading.textContent || '';
      let id = heading.id;
      if (!id) {
        const base = slugify(text) || 'section';
        const count = seen.get(base) || 0;
        seen.set(base, count + 1);
        id = count === 0 ? base : `${base}-${count}`;
        heading.id = id;
      }
      return { id, text };
    });

    setEntries(built);

    if (built.length === 0) return;

    const observer = new IntersectionObserver(
      (observedEntries) => {
        observedEntries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleIds.current.add(entry.target.id);
          } else {
            visibleIds.current.delete(entry.target.id);
          }
        });

        const firstVisible = built.find((entry) => visibleIds.current.has(entry.id));
        if (firstVisible) setActiveId(firstVisible.id);
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [containerRef]);

  if (entries.length < 2) return null;

  const handleClick = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: 'smooth' });
    window.history.pushState(null, '', `#${id}`);
  };

  return (
    <Wrapper aria-label="Table of contents">
      <ToggleButton type="button" onClick={() => setIsOpen((open) => !open)} aria-expanded={isOpen}>
        <HeaderTitle>{title}</HeaderTitle>
        <IconWrapper>{isOpen ? <FaChevronUp /> : <FaChevronDown />}</IconWrapper>
      </ToggleButton>
      <List $isOpen={isOpen}>
        {entries.map((entry) => (
          <ListItem key={entry.id}>
            <TocLink
              href={`#${entry.id}`}
              $isActive={activeId === entry.id}
              onClick={(e) => handleClick(e, entry.id)}
            >
              {entry.text}
            </TocLink>
          </ListItem>
        ))}
      </List>
    </Wrapper>
  );
}

const Wrapper = styled.nav`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-left: 4px solid ${COLORS.brandGreen};
  border-radius: 8px;
  margin: 0 0 40px;
  padding: 20px 24px;

  @media (max-width: 767px) {
    padding: 16px 20px;
    margin: 0 0 32px;
  }
`;

const ToggleButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const HeaderTitle = styled.span`
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
`;

const IconWrapper = styled.span`
  color: ${COLORS.brandGreen};
  font-size: 14px;
  flex-shrink: 0;
`;

const List = styled.ol<{ $isOpen: boolean }>`
  list-style: none;
  margin: ${(props) => (props.$isOpen ? '16px 0 0' : '0')};
  padding: 0;
  max-height: ${(props) => (props.$isOpen ? '1200px' : '0')};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: max-height 0.3s ease, margin 0.3s ease;
`;

const ListItem = styled.li``;

const TocLink = styled.a<{ $isActive: boolean }>`
  display: block;
  font-family: Lato, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: ${(props) => (props.$isActive ? COLORS.brandGreen : '#555')};
  font-weight: ${(props) => (props.$isActive ? 600 : 400)};
  text-decoration: none;
  padding: 4px 0 4px 12px;
  border-left: 2px solid ${(props) => (props.$isActive ? COLORS.brandGreen : 'transparent')};
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: ${COLORS.brandGreen};
  }
`;
